import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  HostBinding,
  inject,
  model,
  OnChanges,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  IPagination,
  IQualificationCity,
  IQualificationMarker,
  IUser,
  IUserQualificationCity,
  IUserQualificationMarker,
  IValuation,
} from '@core/models';
import { Authentication, City, Geomarker } from '@core/services';
import { lastValueFrom } from 'rxjs';
import { RatingEdit } from '../modals';
import { TranslateModule } from '@ngx-translate/core';
import { ValorationDetailComment } from './valoration-detail-comment/valoration-detail-comment';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { ValorationCard } from '../cards';
import { ValorationDetailItem } from './valoration-detail-item/valoration-detail-item';

/**
 * ### **ValorationsDetailComponent**
 *
 * **Selector:** ``<app-valorations-detail>``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar la sección de Valoracione y comentarios para una ciudad o geomarcador.
 */
@Component({
  selector: 'app-valorations-detail',
  imports: [
    TranslateModule,
    ValorationDetailComment,
    LazyLoadImageModule,
    ValorationCard,
    ValorationDetailItem,
  ],
  templateUrl: './valorations-detail.html',
  styleUrl: './valorations-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValorationsDetail implements OnChanges {
  private readonly auth = inject(Authentication);
  private readonly cityService = inject(City);
  private readonly markerService = inject(Geomarker);
  private readonly matDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /** HostBindinig: permite establecer propiedades en el elemento o componente, en este caso definir clases Css sobre el mismo*/
  @HostBinding('class') class = 'row px-3';

  /** Variable que recibe desde un componente padre la lista de valoraciones*/
  readonly valorations = model<IValuation[]>([]);
  /** Variable que recibe desde un componente padre el identificador de la ciudad*/
  readonly city = model<number | null>(null);
  /** Variable que recibe desde un componente padre el identificador del geomarcador*/
  readonly marker = model<number | null>(null);
  /** Variable que recibe desde un componente padre la cantidad de valoraciones*/
  readonly valuations = model<number>(0);

  /** Variable que guarda el estado de autenticación del usuario*/
  protected readonly authenticated = signal<boolean>(false);
  /** VAriable que guardará la información del usuario*/
  protected readonly user = signal<IUser | null>(null);
  /** Variable que guardará la calificaión para la ciudad hecha por un usuario*/
  private userQualificationCity!: IUserQualificationCity | null;
  /** Variable que guardará las valoraciones hecha por un usuario a un geomarcador*/
  private userQualificationMarker!: IUserQualificationMarker | null;
  /** Variable que guardará la calificacion*/
  protected readonly userQualification = signal<number | null>(null);
  /** Variable que guardará el estado si ha sido calificado*/
  protected readonly hasQualification = signal<boolean>(false);
  /** Variable que guardará el path con la url para obtener las demas calificaciones*/
  private nextPageValuation!: string;
  /** Variable que guardará las valoraciones*/
  protected readonly resultValuations = signal<IPagination<any[]> | null>(null);
  /** Señal que controla la solicitud de valoraciones */
  protected readonly requestValuations = signal<boolean>(false);

  constructor() {
    this.auth.onAuthenticate.pipe(takeUntilDestroyed()).subscribe((response) => {
      this.hasQualification.set(false);
      this.userQualification.set(null);
      this.authenticated.set(response);
    });

    this.auth.onUpdateUser.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.user.set(user);
      if (user) {
        this.hasQualification.set(false);
        this.getUserQualification();
        this.valorations.set([]);
        this.getValuations();
      }
    });
  }

  /**
   * Función nativa de angular que se activa cada vez que detecta un cambio sobre las variables de
   * tipo input, en este caso toma en cuenta a la ciudad y dependiendo de si existe un cambio o no hace
   * uso de las funciones this.getUserQualification(), this.getValuations(), asi mismo para el geomarcador.
   * @param changes Parametro que detecta los cambios.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.city) {
      if (this.city() && this.user()) {
        this.marker.set(null);
        this.userQualificationMarker = null;
        this.getUserQualification();
        this.valorations.set([]);
        this.getValuations();
      } else if (this.city()) {
        this.marker.set(null);
        this.userQualificationMarker = null;
        this.valorations.set([]);
        this.getValuations();
      }
    }

    if (changes.marker) {
      if (this.marker() && this.user()) {
        this.city.set(null);
        this.userQualificationCity = null;
        this.getUserQualification();
        this.valorations.set([]);
        this.getValuations();
      } else if (this.marker()) {
        this.city.set(null);
        this.userQualificationCity = null;
        this.valorations.set([]);
        this.getValuations();
      }
    }
  }

  /**
   * Función asincrona que oobtiene la calificacion de una ciudad o un geomarcador para un usuario,
   * asi mismo pregunta dependiendo los parametros hace el proceso ya sea para una ciudad o un gemomarcador, una
   * vez recibe la respuesta empieza a inicializar las variables correspondientes.
   */
  private async getUserQualification(): Promise<void> {
    let qualificationData;
    const city = this.city();
    if (city) {
      try {
        const response = await lastValueFrom(
          this.cityService.qualificationByUser(city, Number(this.user()?.id)),
        );
        qualificationData = response;
        this.userQualificationCity = qualificationData as IUserQualificationCity;
        this.userQualificationMarker = null;
        this.userQualification.set(+this.userQualificationCity.score);
        this.hasQualification.set(true);
      } catch (error) {
        console.error('Error fetching user qualification:', error);
        this.userQualificationCity = null;
        this.userQualification.set(null);
        this.hasQualification.set(false);
      }
    } else if (this.marker()) {
      try {
        const response = await lastValueFrom(
          this.markerService.qualificationByUser(Number(this.marker()), Number(this.user()?.id)),
        );
        qualificationData = response;

        this.userQualificationMarker = qualificationData as IUserQualificationMarker;
        this.userQualificationCity = null;
        this.userQualification.set(+this.userQualificationMarker.score);
        this.hasQualification.set(true);
      } catch (error) {
        console.error('Error fetching user qualification:', error);
        this.userQualificationMarker = null;
        this.userQualification.set(null);
        this.hasQualification.set(false);
      }
    }
    this.markerService.onUpdateRatings();
  }

  /**
   * Función encargada de guardar la calificación ya sea para una ciudad o un geomarcador, en caso de no tener
   * la calificación la agrega, si ya existe una entonces la edita, por ultimo llama al getUserQualification() para
   * actualizar los datos en el frontend.
   */
  private async saveQualification(): Promise<void> {
    let data;
    const city = this.city();
    if (city) {
      data = {
        score: `${this.userQualification()}`,
        user: this.user()?.id,
        city,
      };

      if (this.userQualificationCity) {
        try {
          await lastValueFrom(
            this.cityService.editQualification(this.userQualificationCity.id, data),
          );
          this.getUserQualification();
        } catch (error) {
          console.error('Error updating user qualification:', error);
        }
      } else {
        try {
          await lastValueFrom(this.cityService.addQualification(data as IQualificationCity));
          this.getUserQualification();
        } catch (error) {
          console.error('Error adding user qualification:', error);
        }
      }
    } else {
      data = {
        score: `${this.userQualification()}`,
        user: this.user()?.id,
        marker: this.marker(),
      };

      if (this.userQualificationMarker) {
        try {
          await lastValueFrom(
            this.markerService.editQualification(
              this.userQualificationMarker.id,
              data as IQualificationMarker,
            ),
          );
          this.getUserQualification();
        } catch (error) {
          console.error('Error updating user qualification:', error);
        }
      } else {
        try {
          await lastValueFrom(this.markerService.addQualification(data as IQualificationMarker));
          this.getUserQualification();
        } catch (error) {
          console.error('Error adding user qualification:', error);
        }
      }
    }
  }

  /**
   * Función encargada de abrir el modal que me permite editar la caificación, dependiendo la respuesta este
   * puede eliminar la calificación o guardar la nueva calificación.
   */
  private openEditQualificationModal(): void {
    const dialgogRef = this.matDialog.open(RatingEdit, {
      data: {
        qualification: this.userQualification(),
      },
    });

    dialgogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value) {
          if (value.deleted) {
            this.deleteQualification();
          } else {
            this.userQualification.set(value.qualification);
            this.saveQualification();
          }
        }
      });
  }

  /**
   * Función asincrona que se encarga de eliminar la calificación, para esto necesita el id de la misma, si hay una ciudad
   * eliminara su respectiva calificación, si no, entonces entenderá que es un geomarcador. por ultimo llama al metodo de
   * getUserQualification(), para actualizar la calificación.
   */
  private async deleteQualification(): Promise<void> {
    if (this.city()) {
      try {
        await lastValueFrom(
          this.cityService.deleteQualification(Number(this.userQualificationCity?.id)),
        );
        this.userQualification.set(null);
        this.getUserQualification();
      } catch (error) {
        console.error('Error deleting user qualification:', error);
      }
    } else {
      try {
        await lastValueFrom(
          this.markerService.deleteQualification(Number(this.userQualificationMarker?.id)),
        );
        this.userQualification.set(null);
        this.getUserQualification();
      } catch (error) {
        console.error('Error deleting user qualification:', error);
      }
    }
  }

  /**
   * Función asincrona que se encarga de obtener las valoraciones ya sea para una ciudad o una valoración,
   * para esto se usa la condicional sobre el parametro this.city, en caso de haber valoraciones en la
   * variable valuationsData y no ser la ultima pagina de comentarios
   * @param url parametro que contiene el path de donde se va a obtener la información.
   * @param reset parametro de estado que indica si hay mas valoracionespor llamar.
   */
  protected async getValuations(url?: string, reset?: boolean): Promise<void> {
    this.requestValuations.set(true);
    const city = this.city();
    if (city) {
      try {
        const response = await lastValueFrom(this.cityService.getValuations(city, url));
        const valuationsData = response;
        this.resultValuations.set(valuationsData as IPagination<any[]>);
        if (valuationsData) {
          if (this.nextPageValuation && this.valorations() && !reset) {
            this.valorations.update((valorations) => [...valorations, ...valuationsData.results]);
            this.valuations.update((current) => current + 1);
          } else {
            this.valorations.set(valuationsData.results);
            this.valuations.set(valuationsData.results.length);
          }
          this.nextPageValuation = valuationsData.next;
        }
      } catch (error) {
        console.error('Error fetching city valuations:', error);
      } finally {
        this.requestValuations.set(false);
      }
    } else if (this.marker()) {
      try {
        const response = await lastValueFrom(
          this.markerService.getValuations(Number(this.marker()), url),
        );
        const valuationsData = response;
        this.resultValuations.set(valuationsData as IPagination<any[]>);

        if (valuationsData) {
          if (this.nextPageValuation && this.valorations() && !reset) {
            this.valorations.update((valorations) => [...valorations, ...valuationsData.results]);
            this.valuations.update((current) => current + 1);
          } else {
            this.valorations.set(valuationsData.results);
            this.valuations.set(valuationsData.results.length);
          }
          this.nextPageValuation = valuationsData.next;
        }
      } catch (error) {
        console.error('Error fetching marker valuations:', error);
      } finally {
        this.requestValuations.set(false);
      }
    }
  }

  /**
   * Función que se activa cada que el usuario necesita ver mas valoraciones ya sea un ciudad o un geomarcador,
   * en este caso llama al método que obtienela valoracione y le pasa por parametro la url de la siguiente página
   * de calificaciones.
   */
  protected showMoreValuations(): void {
    this.getValuations(this.nextPageValuation);
  }

  /**
   * Función que se activa cada que el usuario quiera calificar o comentar en una ciudad o geomarcador y
   * no se encuentre logueado, su funcion es mostrar el popup de inicio de sesion.
   */
  protected openAuthForm(): void {
    this.auth.openLoginForm.next(true);
  }

  /**
   * Función encargada de editar o guardar una calificaión, en caso de ser una nueva la guarda haciendo uso del
   * método saveQualification(), si ya existe una calificación procede a ejecutar el método openEditQualificationModal(),
   * si no se encuentra logueado al realizar esta operacion llamará al método de openAuthForm().
   */
  protected editCalification(): void {
    if (this.authenticated()) {
      if (!this.hasQualification()) {
        this.saveQualification();
      } else {
        this.openEditQualificationModal();
      }
    } else {
      this.openAuthForm();
    }
  }
}
